import { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import {
  AudioLines,
  ChevronLeft,
  MessageCircleMore,
  Sparkles,
} from "lucide-react";
import { Box, alpha } from "@mui/material";
import ChartBoxCode from "./ChartBoxCode";
import { getUserProfile } from "../../utils/auth";
import { showToast, hideToast } from "../../utils/toastManager";
import OrbieeFlow from "./OrbieeFlow";
import {
  AgentPhase,
  buildChatBubbleTextsFromResponse,
  flagInterimSegments,
  subscribeOrbieeBackendWelcome,
  subscribeChatFollowUps,
  isSessionTakenOver,
  subscribeSessionTakeover,
  resumeSessionAfterTakeover,
  connectOrbieeChatSocket,
} from "../../services/websocket";
import { useCart } from "../../context/CartContext";
import { useWishlist } from "../../context/WishlistContext";
import { applyWishlistFromAgentResponse } from "../../utils/applyWishlistFromAgentResponse";
import { applyCartFromAgentResponse } from "../../utils/agentCartPayload";
import { startVoiceRecording, convertBuffersToWAV } from "./AudioCode";
import {
  isValidTranscription,
  isEmptyTranscriptError,
  unlockOrbieeVoicePlayback,
  stopOrbieeVoicePlayback,
  subscribeOrbieeVoicePlayback,
  isOrbieeVoicePlaying,
} from "../../services/websocket";
import { detectLanguageCode } from "../../utils/detectLanguage";
import ConnectToRealTimeAvatargetVideo from "./ConnectToRealTimeAvatargetVideo";
import VoiceModeControls from "./VoiceModeControls";
import VoiceModeVisual from "./VoiceModeVisual";
import VirtualTryOn from "../VirtualTryOn";
import { notifyVirtualTryOnClosed } from "../../utils/virtualTryOnAvatarHold";
import useDraggablePosition from "../../hooks/useDraggablePosition";
import {
  claimAssistantMode,
  releaseAssistantMode,
  setAssistantChatOpen,
  subscribeAssistantMode,
} from "../../services/assistantMode";
import {
  claimInlineTryOn,
  closeInlineTryOns,
  isProductRoute,
} from "../../utils/inlineTryOn";

// The thread opens empty and only the backend's connect-greeting fills it
// (2026-10-28). There is no local greeting any more: the app used to seed
// "Hi Isabela, I'm Orbiee…" and swap it for the backend's when that arrived,
// so a backend that sent none still showed copy of ours. Now no frame, no
// bubble — and the avatar follows the same rule for what it speaks.

/**
 * "Signed in elsewhere" — the toast half of the notice, for when the panel
 * is closed. Pinned (`duration: 0`): it asks for a decision, so it does not
 * time out, and three cart toasts cannot bury it. Its button is the same
 * "Continue here" as the panel's; pressing it slides the toast away and
 * runs the one resume handler, which brings the toast back as "Try again"
 * if the socket does not open. One id for both of those states, so a
 * repeat replaces rather than stacks; the success note has its own id so it
 * can follow a toast that is still sliding out.
 */
const SESSION_TAKEOVER_TOAST_ID = "session-takeover";
const SESSION_RESUMED_TOAST_ID = "session-resumed";

const showSessionTakenToast = (onContinue) =>
  showToast({
    id: SESSION_TAKEOVER_TOAST_ID,
    severity: "warning",
    title: "Signed in on another device",
    message:
      "Your connection here was ended. Continue here to keep chatting on this device.",
    duration: 0,
    action: { label: "Continue here", onClick: onContinue },
  });

const showSessionResumeFailedToast = (onRetry) =>
  showToast({
    id: SESSION_TAKEOVER_TOAST_ID,
    severity: "error",
    title: "Couldn't reconnect",
    message: "Check your connection and try again.",
    duration: 0,
    action: { label: "Try again", onClick: onRetry },
  });

const showSessionResumedToast = () =>
  showToast({
    id: SESSION_RESUMED_TOAST_ID,
    severity: "success",
    title: "Back on this device",
    message: "Orbiee is connected here again.",
  });

/** Grace window before handing mic back when a voice turn produces no streamed audio. */
const NO_AUDIO_GRACE_MS = 10000;

/**
 * AvatarLogoCode Component
 * Displays the avatar with video stream, loading states, and controls
 *
 * @param {Object} props - Component props
 * @param {boolean} props.isAvatarRecording - Whether avatar is currently recording
 * @param {Function} props.handleAvatarClick - Handler for avatar click
 * @param {MediaStream|null} props.avatarStream - WebRTC media stream
 * @param {string|null} props.avatarVideoUrl - Avatar video blob URL
 * @param {boolean} props.isAvatarMuted - Whether avatar is muted
 * @param {boolean} props.isAvatarProcessing - Whether avatar is processing
 * @param {number} props.avatarLoadingMessageIndex - Index of current loading message
 * @param {boolean} props.isAvatarConnecting - Whether avatar is connecting
 * @param {Function} props.toggleAvatarMute - Function to toggle mute state
 * @param {Function} props.forcePlayAvatarVideo - Function to force play video
 * @param {Function} props.ensureAvatarPlayback - Function to ensure video playback
 * @param {Function} props.setAvatarVideoUrl - Function to set avatar video URL
 * @param {Object} props.avatarVideoRef - Ref for video element
 * @param {Object} props.avatarCanvasRef - Ref for canvas element
 * @param {boolean} props.showControlBar - Whether to show the control bar
 * @param {Function} props.setShowControlBar - Function to set control bar visibility
 * @param {boolean} props.isListening - Whether currently listening
 * @param {Function} props.handleControlBarMicClick - Handler for microphone button click
 * @param {Function} props.handleControlBarChatClick - Handler for chat button click
 * @param {string} [props.languageCode="eng"] - Default language code for chat/API (e.g. "eng", "spa"); voice uses STT-detected language when available
 */
export default function AvatarLogoCode({
  isAvatarRecording = false,
  handleAvatarClick,
  avatarStream = null,
  avatarVideoUrl = null,
  isAvatarMuted = false,
  isAvatarProcessing = false,
  avatarLoadingMessageIndex = 0,
  isAvatarConnecting = false,
  toggleAvatarMute,
  forcePlayAvatarVideo,
  ensureAvatarPlayback,
  setAvatarVideoUrl,
  avatarVideoRef,
  avatarCanvasRef,
  showControlBar: externalShowControlBar,
  setShowControlBar: externalSetShowControlBar,
  isListening = false,
  handleControlBarMicClick,
  handleControlBarChatClick,
  languageCode = "eng",
}) {
  const navigate = useNavigate();
  const { addToCart, replaceCartItemsFromAgent } = useCart();
  const wishlist = useWishlist();

  // Desktop: the widget can be dragged anywhere and remembers where it was
  // dropped. Phones keep the fixed bottom-right corner from the sx below.
  const drag = useDraggablePosition({
    storageKey: "orbiee_avatar_widget_pos",
    topVar: "--ob-topbar-h",
  });

  // State to track if avatar video is loading after login
  const [isAvatarVideoLoading, setIsAvatarVideoLoading] = useState(false);

  // Internal state for showControlBar if not provided as prop
  const [internalShowControlBar, setInternalShowControlBar] = useState(false);

  // Use external state if provided, otherwise use internal state
  const showControlBar =
    externalShowControlBar !== undefined
      ? externalShowControlBar
      : internalShowControlBar;
  const setShowControlBar =
    externalSetShowControlBar || setInternalShowControlBar;

  // Internal state for chat box
  // Check sessionStorage on mount to restore chat state after navigation
  const [isChatOpen, setIsChatOpen] = useState(() => {
    // Check if user manually closed the chat
    const wasManuallyClosed =
      sessionStorage.getItem("orbiee_chat_manually_closed") === "true";

    // If manually closed, don't auto-open
    if (wasManuallyClosed) {
      return false;
    }

    // Check if chat should be open after navigation
    const shouldBeOpen =
      sessionStorage.getItem("orbiee_chat_should_be_open") === "true";
    if (shouldBeOpen) {
      // Clear the flag after reading
      sessionStorage.removeItem("orbiee_chat_should_be_open");
      return true;
    }
    return false;
  });

  // Empty until the backend greets (applyBackendWelcomeToChat) or the
  // shopper speaks. No seeded greeting — see the note at the top.
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInputValue, setChatInputValue] = useState("");
  const [isChatLoading, setIsChatLoading] = useState(false);
  // What the agent is doing in the turn in flight ({ phase, tool }), straight
  // from the socket frames — the loader's text. Null until the turn is sent.
  const [chatActivity, setChatActivity] = useState(null);
  // The backend's follow-up suggestions for the last typed turn — the chips
  // under the thread. Chat only (subscribeChatFollowUps drops voice and
  // avatar turns); cleared when the next turn leaves, so they never outlive
  // the reply they belong to.
  const [chatFollowUps, setChatFollowUps] = useState([]);
  useEffect(() => subscribeChatFollowUps(setChatFollowUps), []);

  // "Signed in elsewhere" (services/websocket/sessionTakeover.js): the
  // backend moved this account's agent session to another device; the
  // socket is cut and will not reconnect by itself. The shopper stays
  // signed in, and two things offer the way back — the notice in the panel
  // and a pinned toast for when the panel is closed — both through
  // handleResumeSession (2026-10-28 — it used to sign this device out, and
  // the toast lived in AuthContext). `status`: "taken" (notice up),
  // "connecting" (a button pressed, socket opening), "failed" (it did not
  // open; the buttons read "Try again"). Null while nothing is up. The
  // register is the truth for the socket side; this is only what shows.
  const [sessionTakeover, setSessionTakeover] = useState(() =>
    isSessionTakenOver() ? { status: "taken" } : null
  );
  // The toasts' buttons reach the handler through a ref: the first toast is
  // raised inside a subscription that must not re-subscribe per render, and
  // the "Try again" one is raised by the handler itself.
  const resumeSessionRef = useRef(null);
  const resumeInFlightRef = useRef(false);
  useEffect(
    () =>
      subscribeSessionTakeover(() => {
        setSessionTakeover({ status: "taken" });
        // Chips would send a turn the socket can no longer carry.
        setChatFollowUps([]);
        showSessionTakenToast(() => resumeSessionRef.current?.());
      }),
    []
  );
  const handleResumeSession = useCallback(async () => {
    const customerId = getUserProfile()?.customer_id;
    if (!customerId) return;
    // The panel's button and the toast's can be pressed in turn: one
    // attempt at a time, the second press is absorbed.
    if (resumeInFlightRef.current) return;
    resumeInFlightRef.current = true;
    // The toast's own press has already dismissed it; the panel's has not.
    hideToast(SESSION_TAKEOVER_TOAST_ID);
    setSessionTakeover({ status: "connecting" });
    try {
      // Lift the refusal first; the connect then opens a fresh socket,
      // which is what makes the backend hand the session back (and cut the
      // other device). `connectOrbieeChatSocket` never rejects — it reports.
      resumeSessionAfterTakeover();
      const connected = await connectOrbieeChatSocket(customerId);
      if (connected) {
        setSessionTakeover(null);
        showSessionResumedToast();
      } else if (!isSessionTakenOver()) {
        setSessionTakeover({ status: "failed" });
        showSessionResumeFailedToast(() => resumeSessionRef.current?.());
      }
      // Else: taken again while the socket was opening — the subscriber
      // above has already put the notice and the toast back up.
    } finally {
      resumeInFlightRef.current = false;
    }
  }, []);
  useEffect(() => {
    resumeSessionRef.current = handleResumeSession;
  }, [handleResumeSession]);

  // The backend's connect-greeting opens the thread — the only greeting
  // there is. It fills an empty thread and leaves a thread with messages
  // alone: the greeting is a session opener, and the socket re-sends one on
  // every connect (a reconnect, "Continue here"), which must not land in
  // the middle of a conversation. `subscribeOrbieeBackendWelcome` replays a
  // cached greeting on subscribe, so a widget that mounts after the frame
  // still shows it.
  const applyBackendWelcomeToChat = useCallback((backendText) => {
    const text = String(backendText ?? "").trim();
    if (!text) return;
    setChatMessages((prev) =>
      prev.length
        ? prev
        : [
            {
              id: 1,
              conversation_text: text,
              answer: text,
              isUser: false,
              timestamp: new Date(),
            },
          ]
    );
  }, []);
  useEffect(
    () => subscribeOrbieeBackendWelcome(applyBackendWelcomeToChat),
    [applyBackendWelcomeToChat]
  );

  // Listen for background chat events (from realtime avatar) and append to chat
  useEffect(() => {
    const handleBackgroundChat = (event) => {
      const { userText, botText, botTexts, botPreambles } = event.detail || {};
      const newMessages = [];
      const baseId = Date.now();

      if (userText) {
        newMessages.push({
          id: baseId,
          content: userText,
          isUser: true,
          timestamp: new Date(),
        });
      }

      const assistantTexts = Array.isArray(botTexts) && botTexts.length
        ? botTexts.map((t) => String(t ?? "").trim()).filter(Boolean)
        : botText
          ? [String(botText).trim()]
          : [];

      // The avatar/voice path renders the whole turn at once, so without
      // this its preambles ("Closing the try-on for you…") read as answers.
      const interim = flagInterimSegments(assistantTexts, botPreambles);
      assistantTexts.forEach((text, i) => {
        newMessages.push({
          id: `${baseId}-bot-${i}`,
          conversation_text: text,
          answer: text,
          isUser: false,
          isLlmSegment: true,
          isInterim: interim[i],
          timestamp: new Date(),
        });
      });

      if (newMessages.length) {
        setChatMessages((prev) => [...prev, ...newMessages]);
      }
    };

    window.addEventListener("orbieeBackgroundChat", handleBackgroundChat);
    return () =>
      window.removeEventListener("orbieeBackgroundChat", handleBackgroundChat);
  }, []);

  // Voice mode state
  const [isVoiceMode, setIsVoiceMode] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isMicMuted, setIsMicMuted] = useState(false);
  const [audioLevel, setAudioLevel] = useState(0);
  /** True while streamed assistant reply audio is playing (voice visual "speaking"). */
  const [isAssistantSpeaking, setIsAssistantSpeaking] = useState(false);
  const recordingRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const isVoiceModeRef = useRef(false); // Track voice mode state to avoid stale closures
  const isRecordingRef = useRef(false);
  const isChatLoadingRef = useRef(false);
  const isMicMutedRef = useRef(false);
  const voiceRestartTimerRef = useRef(null);
  const startVoiceModeRef = useRef(null);
  const activeStreamIdRef = useRef(null);

  // Virtual Try-On state
  const [tryOnOpen, setTryOnOpen] = useState(false);
  const [tryOnProductId, setTryOnProductId] = useState(null);
  /** Mode the try-on should launch straight into, or null to ask first. */
  const [tryOnAutoStart, setTryOnAutoStart] = useState(null);
  /** Polls spent waiting for a product page to claim an agent try-on. */
  const inlineWaitRef = useRef(0);


  // Track chat state in sessionStorage whenever it changes
  useEffect(() => {
    // Store current chat state in sessionStorage so OrbieeFlow can check it
    if (isChatOpen) {
      sessionStorage.setItem("orbiee_chat_is_open", "true");
      // Only clear manually closed flag if chat is explicitly opened (not just state change)
      // We'll handle clearing it in handleChatClick and startVoiceMode instead
    } else {
      sessionStorage.removeItem("orbiee_chat_is_open");
      // Don't set manually closed flag here - it's set explicitly in onClose handler
    }
    // An open panel takes the conversation from the avatar's ambient mic
    // (services/assistantMode.js). Driven by state, not the click handler,
    // so a panel restored after navigation or a reload counts too.
    setAssistantChatOpen(isChatOpen);
  }, [isChatOpen]);

  // Split screen: the page makes room for the docked panel. The width and the
  // phone behaviour (no reflow, no scroll behind a full-screen panel) live in
  // CSS (`body.ob-chat-docked`, orbiee.css §11b) so they always match the
  // panel and follow a window resize. A class, not inline styles, also keeps
  // clear of MUI's scroll lock, which writes and restores body's inline
  // padding/overflow while a Dialog or Popover is open.
  useEffect(() => {
    if (isChatOpen) document.body.style.transition = "padding-right 0.3s ease";
    document.body.classList.toggle("ob-chat-docked", isChatOpen);
    return () => {
      document.body.classList.remove("ob-chat-docked");
    };
  }, [isChatOpen]);

  // Check sessionStorage for virtual try-on trigger
  useEffect(() => {
    const clearTryOnFlags = () => {
      sessionStorage.removeItem("orbiee_virtual_try_on_open");
      sessionStorage.removeItem("orbiee_virtual_try_on_product_id");
      sessionStorage.removeItem("orbiee_virtual_try_on_autoclick_profile");
      sessionStorage.removeItem("orbiee_virtual_try_on_close_reason");
    };

    const checkVirtualTryOn = () => {
      const tryOnOpenFlag = sessionStorage.getItem("orbiee_virtual_try_on_open");
      const productId = sessionStorage.getItem(
        "orbiee_virtual_try_on_product_id"
      );

      // Check if we should open the try-on
      if (tryOnOpenFlag === "true" && productId) {
        // An agent-driven try-on must not stop and ask which model to use —
        // that breaks the conversation loop. The flag OrbieeFlow set becomes
        // the mode the surface starts rendering with, immediately.
        const auto =
          sessionStorage.getItem("orbiee_virtual_try_on_autoclick_profile") ===
          "true";
        const autoStart = auto ? "custom_model" : null;

        // A mounted page for this product shows the mirror inline, beside
        // the product details: no popup.
        if (claimInlineTryOn(productId, { autoStart })) {
          inlineWaitRef.current = 0;
          clearTryOnFlags();
          return;
        }
        // The agent usually navigates to the product page in the same turn;
        // give that page a few polls to mount and claim the try-on before
        // falling back to the popup.
        if (isProductRoute(productId) && inlineWaitRef.current < 6) {
          inlineWaitRef.current += 1;
          return;
        }
        inlineWaitRef.current = 0;

        setTryOnAutoStart(autoStart);
        setTryOnProductId(productId);
        setTryOnOpen(true);
        clearTryOnFlags();
      }
      // Check if we should close the try-on
      else if (tryOnOpenFlag === "false") {
        // Adding to the bag used to end the try-on because the popup covered
        // the page. The inline mirror can stay: the shopper is still looking
        // at what they just added.
        const reason = sessionStorage.getItem("orbiee_virtual_try_on_close_reason");
        if (reason !== "add_to_cart") closeInlineTryOns();

        setTryOnOpen(false);
        setTryOnProductId(null);
        setTryOnAutoStart(null);
        notifyVirtualTryOnClosed();
        clearTryOnFlags();
      }
    };

    // Check immediately
    checkVirtualTryOn();

    // Also check periodically in case the flag was set before this component mounted
    const interval = setInterval(checkVirtualTryOn, 500);

    return () => clearInterval(interval);
  }, []);


  // Handle chat button click
  const handleChatClick = () => {
    if (handleControlBarChatClick) {
      handleControlBarChatClick();
    } else {
      // Default behavior: open chat box
      setIsChatOpen(true);
      setShowControlBar(false);
      // Clear manual close flag when user opens chat
      sessionStorage.removeItem("orbiee_chat_manually_closed");
    }
  };

  /** Replace in-flight LLM segment bubbles for the current turn (one box per llm_response). */
  const applyLlmDisplaySegments = useCallback((segments, streamId, preambles) => {
    if (!streamId || !Array.isArray(segments)) return false;
    const texts = segments
      .map((text) => String(text ?? "").trim())
      .filter(Boolean);
    if (!texts.length) return false;
    const interim = flagInterimSegments(texts, preambles);

    const prefix = `llm-${streamId}-`;
    setChatMessages((prev) => {
      const kept = prev.filter((m) => !String(m.id).startsWith(prefix));
      const bubbles = texts.map((text, i) => ({
        id: `${prefix}${i}`,
        conversation_text: text,
        answer: text,
        isUser: false,
        isLlmSegment: true,
        // A tool preamble — "let me look that up" — not the answer. Tagged
        // by the backend; see flagInterimSegments for the fallback rule.
        isInterim: interim[i],
        timestamp: new Date(),
      }));
      return [...kept, ...bubbles];
    });
    return true;
  }, []);

  // Handle sending message (typed text or voice via WebSocket `voice` frame)
  // sendOptions.audioBlob: WAV blob — STT + agent turn on backend
  // sendOptions.languageCode: optional override when transcript returns language_code
  const handleSendMessage = useCallback(
    async (textToSend = null, sendOptions = {}) => {
      const voiceBlob =
        sendOptions.audioBlob instanceof Blob ||
        sendOptions.audioBlob instanceof File
          ? sendOptions.audioBlob
          : null;
      const messageText = voiceBlob ? "" : textToSend || chatInputValue.trim();
      if ((!voiceBlob && !messageText) || isChatLoading) return;

      // A new turn makes the last reply's suggestions stale.
      setChatFollowUps([]);

      const langForApi = voiceBlob
        ? sendOptions.languageCode ?? languageCode
        : sendOptions.languageCode ??
          detectLanguageCode(messageText) ??
          languageCode;

      const streamId = Date.now();
      activeStreamIdRef.current = streamId;
      let displayedLlmSegments = false;
      let userMessageAdded = false;

      const appendUserTranscript = (text) => {
        if (!isValidTranscription(text) || userMessageAdded) return;
        userMessageAdded = true;
        setChatMessages((prev) => [
          ...prev,
          {
            id: streamId,
            content: text,
            isUser: true,
            timestamp: new Date(),
          },
        ]);
      };

      // Typed chat: show user bubble immediately
      if (!voiceBlob) {
        // Typing is the shopper choosing the panel: if the avatar's mic was
        // listening beside it (tapped by hand while the panel was open),
        // that loop stands down so one turn does not race the other.
        claimAssistantMode("chat", "typed-message");
        setChatMessages((prev) => [
          ...prev,
          {
            id: streamId,
            content: messageText,
            isUser: true,
            timestamp: new Date(),
          },
        ]);
        userMessageAdded = true;
        if (!textToSend) {
          setChatInputValue("");
        }
      }

      // Start from a clean line, never the previous turn's last step. A voice
      // turn is transcribing from the moment the audio leaves, before the
      // socket has said anything.
      setChatActivity(
        voiceBlob ? { phase: AgentPhase.TRANSCRIBING, tool: null } : null
      );
      setIsChatLoading(true);

      const onLlmDisplayUpdate = (segments, preambles) => {
        if (applyLlmDisplaySegments(segments, streamId, preambles)) {
          displayedLlmSegments = true;
        }
      };

      const onActivity = (activity) => {
        if (activeStreamIdRef.current === streamId) setChatActivity(activity);
      };

      const flowPayload = voiceBlob
        ? { audioBlob: voiceBlob }
        : { content: messageText };

      try {
        const userProfile = getUserProfile();
        const customerId = userProfile?.customer_id;

        const conversationText = await OrbieeFlow(
          flowPayload,
          customerId,
          navigate,
          addToCart,
          langForApi,
          (lastResponse) => {
            // Agent cart snapshot → UI (no Cart API)
            applyCartFromAgentResponse(
              replaceCartItemsFromAgent,
              lastResponse
            );
            const bubbles = buildChatBubbleTextsFromResponse(lastResponse);
            if (
              applyLlmDisplaySegments(
                bubbles,
                streamId,
                lastResponse?.llm_preamble_messages
              )
            ) {
              displayedLlmSegments = true;
            }
            // State, plus the site toast and fly-to-heart for a save —
            // OrbieeFlow decides whether this turn lands on /wishlist,
            // because it is the one that sees every intent.
            applyWishlistFromAgentResponse(lastResponse, wishlist);
            if (voiceBlob) {
              appendUserTranscript(
                lastResponse?.user_transcription || lastResponse?.transcription
              );
            }
          },
          {
            // Chat-box surfaces only: typed → text, mic → voice (avatar widget sets mode:"avatar")
            mode: voiceBlob ? "voice" : "text",
            onLlmDisplayUpdate,
            onActivity,
            languageCode: langForApi,
            onTranscript: voiceBlob
              ? ({ text }) => {
                  appendUserTranscript(text);
                }
              : undefined,
          }
        );

        if (voiceBlob && !userMessageAdded) {
          const errorMessage = {
            id: streamId,
            conversation_text:
              "Sorry, I couldn't understand your voice. Please try again.",
            answer:
              "Sorry, I couldn't understand your voice. Please try again.",
            isUser: false,
            timestamp: new Date(),
          };
          setChatMessages((prev) => [...prev, errorMessage]);
        } else if (!displayedLlmSegments) {
          if (conversationText) {
            const botMessage = {
              id: `llm-${streamId}-0`,
              conversation_text: conversationText,
              answer: conversationText,
              isUser: false,
              timestamp: new Date(),
            };
            setChatMessages((prev) => [...prev, botMessage]);
          } else {
            const botMessage = {
              id: Date.now() + 1,
              conversation_text:
                "I'm sorry, I couldn't process your message. Please make sure you're logged in.",
              answer:
                "I'm sorry, I couldn't process your message. Please make sure you're logged in.",
              isUser: false,
              timestamp: new Date(),
            };
            setChatMessages((prev) => [...prev, botMessage]);
          }
        }
      } catch (error) {
        console.error("Error sending message:", error);
        if (activeStreamIdRef.current === streamId) {
          const prefix = `llm-${streamId}-`;
          setChatMessages((prev) =>
            prev.filter((m) => !String(m.id).startsWith(prefix))
          );
        }
        // Mic closed with silence / STT heard nothing — don't poison the chat UI.
        if (isEmptyTranscriptError(error)) {
          setTimeout(() => {
            if (
              isVoiceModeRef.current &&
              !isMicMutedRef.current &&
              !isRecordingRef.current &&
              typeof startVoiceModeRef.current === "function"
            ) {
              startVoiceModeRef.current();
            }
          }, 400);
          return;
        }
        // The socket was cut under this turn by a takeover: the panel's
        // notice says why and offers the way back, so no error bubble.
        if (isSessionTakenOver()) return;
        const botMessage = {
          id: Date.now() + 1,
          conversation_text:
            "Sorry, there was an error processing your message. Please try again.",
          answer:
            "Sorry, there was an error processing your message. Please try again.",
          isUser: false,
          timestamp: new Date(),
        };
        setChatMessages((prev) => [...prev, botMessage]);
      } finally {
        if (activeStreamIdRef.current === streamId) {
          activeStreamIdRef.current = null;
        }
        setIsChatLoading(false);
      }
    },
    [
      addToCart,
      applyLlmDisplaySegments,
      chatInputValue,
      isChatLoading,
      languageCode,
      navigate,
      wishlist,
      replaceCartItemsFromAgent,
    ]
  );

  // Handle key press in chat input
  const handleChatKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey && chatInputValue.trim()) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Process recording result (extracted to be reusable for automatic silence/timeout stops)
  const processRecordingResult = useCallback(
    async (result) => {
      if (!result) {
        return;
      }

      // Only process if we have audio buffers (don't process empty/muted recordings)
      if (
        result &&
        result.buffers &&
        result.buffers.length > 0 &&
        result.audioContext
      ) {
        try {
          const wavBlob = await convertBuffersToWAV(
            result.buffers,
            result.audioContext.sampleRate || 44100
          );

          await handleSendMessage(null, { audioBlob: wavBlob });
        } catch (transcribeError) {
          console.error("Voice message error:", transcribeError);
          setIsChatLoading(false);
          if (isEmptyTranscriptError(transcribeError)) {
            setTimeout(() => {
              if (
                isVoiceModeRef.current &&
                !isMicMutedRef.current &&
                !isRecordingRef.current &&
                typeof startVoiceModeRef.current === "function"
              ) {
                startVoiceModeRef.current();
              }
            }, 400);
            return;
          }
          const errorMessage = {
            id: Date.now() + 1,
            conversation_text:
              "Sorry, I couldn't understand your voice. Please try again.",
            answer: "Sorry, I couldn't understand your voice. Please try again.",
            isUser: false,
            timestamp: new Date(),
          };
          setChatMessages((prev) => [...prev, errorMessage]);
        }
      } else {
        setIsChatLoading(false);
      }
    },
    [handleSendMessage]
  );

  const clearVoiceRestartTimer = useCallback(() => {
    if (voiceRestartTimerRef.current) {
      clearTimeout(voiceRestartTimerRef.current);
      voiceRestartTimerRef.current = null;
    }
  }, []);

  const cleanupMediaStream = useCallback(() => {
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((track) => track.stop());
      mediaStreamRef.current = null;
    }
  }, []);

  // Schedule mic re-open while still in voice conversation mode
  const scheduleVoiceRestart = useCallback(
    (delayMs = 400) => {
      clearVoiceRestartTimer();
      voiceRestartTimerRef.current = setTimeout(() => {
        voiceRestartTimerRef.current = null;
        // Muted = pause the listen loop; user must unmute to record again.
        if (isMicMutedRef.current) return;
        if (
          isVoiceModeRef.current &&
          !isRecordingRef.current &&
          !isChatLoadingRef.current &&
          !recordingRef.current
        ) {
          if (typeof startVoiceModeRef.current === "function") {
            startVoiceModeRef.current();
          }
        }
      }, delayMs);
    },
    [clearVoiceRestartTimer]
  );

  const voicePlaybackActiveRef = useRef(false);
  const audioGraceTimerRef = useRef(null);

  const clearAudioGraceTimer = useCallback(() => {
    if (audioGraceTimerRef.current != null) {
      clearTimeout(audioGraceTimerRef.current);
      audioGraceTimerRef.current = null;
    }
  }, []);

  // Start voice recording
  const startVoiceMode = useCallback(async () => {
    // Prevent overlapping recordings
    if (recordingRef.current || isRecordingRef.current) {
      return;
    }

    try {
      clearVoiceRestartTimer();

      // Unmute whenever the user intentionally starts a listen loop.
      setIsMicMuted(false);
      isMicMutedRef.current = false;

      // The mic tap is the user gesture that lets the browser play the
      // streamed voice reply for this turn. Safe to call repeatedly.
      unlockOrbieeVoicePlayback();

      // Activate voice mode (controls render below avatar; chat stays optional)
      setIsVoiceMode(true);
      isVoiceModeRef.current = true;
      setIsRecording(true);
      isRecordingRef.current = true;
      // Voice mode owns the conversation from here: the avatar stops its
      // mic loop and goes silent (it is hidden below) until this releases.
      // Idempotent, so the loop's re-listens are free.
      claimAssistantMode("voice", "voice-start");
      // Voice owns the turn now; chips under the thread would answer a reply
      // that is about to be spoken, not read.
      setChatFollowUps([]);

      // Start recording
      const recording = await startVoiceRecording(
        (rms) => {
          // Update audio level for visualization
          setAudioLevel(rms);
        },
        () => {
          // Speech detected callback
        },
        () => isMicMutedRef.current, // Live mute check (avoid stale closure)
        async (result) => {
          // Automatic stop callback (silence/timeout detected)

          // Only process if we have recorded buffers (not muted recording)
          if (!result || !result.buffers || result.buffers.length === 0) {
            recordingRef.current = null;
            setIsRecording(false);
            isRecordingRef.current = false;
            setAudioLevel(0);
            cleanupMediaStream();
            // Keep conversation loop alive — listen again (unless muted)
            if (isVoiceModeRef.current && !isMicMutedRef.current) {
              scheduleVoiceRestart(400);
            }
            return;
          }

          // Clear ref and state
          recordingRef.current = null;
          setIsRecording(false);
          isRecordingRef.current = false;
          // Process the audio and call API
          await processRecordingResult(result);
          // Cleanup
          cleanupMediaStream();
          // Don't set isVoiceMode to false here - keep it active for re-activation
          setAudioLevel(0);
        }
      );

      // Voice mode may have been cancelled / muted while awaiting getUserMedia
      if (!isVoiceModeRef.current || isMicMutedRef.current) {
        try {
          recording.stopRecording();
        } catch (_) {
          /* ignore */
        }
        setIsRecording(false);
        isRecordingRef.current = false;
        cleanupMediaStream();
        return;
      }

      recordingRef.current = recording;
      mediaStreamRef.current = recording.mediaStream;
    } catch (error) {
      console.error("Error starting voice recording:", error);
      alert("Failed to access microphone. Please check permissions.");
      setIsVoiceMode(false);
      isVoiceModeRef.current = false;
      setIsRecording(false);
      isRecordingRef.current = false;
      releaseAssistantMode("voice", "voice-mic-denied");
    }
  }, [
    processRecordingResult,
    clearVoiceRestartTimer,
    cleanupMediaStream,
    scheduleVoiceRestart,
  ]);

  startVoiceModeRef.current = startVoiceMode;

  /** Stop capture and discard buffers — stay in voice mode, send nothing. */
  const discardActiveCapture = useCallback(() => {
    clearVoiceRestartTimer();

    const recording = recordingRef.current;
    recordingRef.current = null;

    if (recording) {
      try {
        recording.stopRecording();
      } catch (error) {
        console.error("Error discarding capture:", error);
      }
    }

    cleanupMediaStream();
    setIsRecording(false);
    isRecordingRef.current = false;
    setAudioLevel(0);
  }, [clearVoiceRestartTimer, cleanupMediaStream]);

  // Red stop button / end conversation: discard audio, send nothing, leave voice mode
  const cancelVoiceRecording = useCallback(() => {
    clearVoiceRestartTimer();
    // Silence any reply still being spoken — the shopper ended the turn.
    stopOrbieeVoicePlayback();

    setIsVoiceMode(false);
    isVoiceModeRef.current = false;
    setIsMicMuted(false);
    isMicMutedRef.current = false;
    setIsRecording(false);
    isRecordingRef.current = false;
    setAudioLevel(0);
    setIsAssistantSpeaking(false);

    const recording = recordingRef.current;
    recordingRef.current = null;

    if (recording) {
      try {
        // Stop capture only — do not process or send to backend
        recording.stopRecording();
      } catch (error) {
        console.error("Error cancelling recording:", error);
      }
    }

    cleanupMediaStream();
    // Falls back to "chat" if the panel is open, else "none". Either way the
    // avatar's loop does not resume by itself — that takes a tap on its mic.
    releaseAssistantMode("voice", "voice-stop");
  }, [clearVoiceRestartTimer, cleanupMediaStream]);

  // The avatar's mic is unreachable while voice mode hides it, but should
  // anything else claim the avatar mid-voice, voice mode yields — one live
  // microphone at a time is the whole point of the mode register.
  useEffect(() => {
    return subscribeAssistantMode(({ mode }) => {
      if (mode === "avatar" && isVoiceModeRef.current) {
        cancelVoiceRecording();
      }
    });
  }, [cancelVoiceRecording]);

  /**
   * Mute: stop recording immediately, discard audio, stay in voice mode.
   * Unmute: start a fresh listen loop (nothing from the muted period is sent).
   */
  const toggleMicMute = useCallback(() => {
    if (isMicMutedRef.current) {
      setIsMicMuted(false);
      isMicMutedRef.current = false;
      if (
        isVoiceModeRef.current &&
        !isRecordingRef.current &&
        !isChatLoadingRef.current
      ) {
        startVoiceMode();
      }
      return;
    }

    setIsMicMuted(true);
    isMicMutedRef.current = true;
    discardActiveCapture();
  }, [discardActiveCapture, startVoiceMode]);

  // Handle voice mode button click
  const handleVoiceModeClick = useCallback(() => {
    if (isRecordingRef.current || recordingRef.current) {
      // End conversation — discard in-progress audio
      cancelVoiceRecording();
    } else {
      startVoiceMode();
    }
  }, [cancelVoiceRecording, startVoiceMode]);

  // Combined handler for microphone button - ensures voice mode is activated
  const handleMicButtonClick = useCallback(() => {
    // Always call the internal handler first to ensure voice mode activates reliably
    handleVoiceModeClick();

    // If external handler is also provided, call it (for any additional functionality)
    if (handleControlBarMicClick && typeof handleControlBarMicClick === 'function') {
      try {
        handleControlBarMicClick();
      } catch (error) {
        console.error("Error in handleControlBarMicClick:", error);
      }
    }
  }, [handleControlBarMicClick, handleVoiceModeClick]);

  // Sync refs with state (avoid stale closures in TTS / restart timers)
  useEffect(() => {
    isVoiceModeRef.current = isVoiceMode;
  }, [isVoiceMode]);

  useEffect(() => {
    isRecordingRef.current = isRecording;
  }, [isRecording]);

  useEffect(() => {
    isChatLoadingRef.current = isChatLoading;
  }, [isChatLoading]);

  useEffect(() => {
    isMicMutedRef.current = isMicMuted;
  }, [isMicMuted]);

  // Assistant finished speaking its streamed reply — reopen the mic if still in voice mode.
  const handleTTSFinished = useCallback(() => {
    if (isMicMutedRef.current) return;
    if (
      isVoiceModeRef.current &&
      !isRecordingRef.current &&
      !isChatLoadingRef.current
    ) {
      scheduleVoiceRestart(500);
    }
  }, [scheduleVoiceRestart]);

  const maybeNotifyVoiceTurnComplete = useCallback(() => {
    if (!isVoiceModeRef.current) return;
    if (isChatLoadingRef.current) return;
    if (voicePlaybackActiveRef.current) return;
    clearAudioGraceTimer();
    handleTTSFinished();
  }, [clearAudioGraceTimer, handleTTSFinished]);

  // Streamed voice reply playback → voice-mode mic hand-back (works with chat closed).
  useEffect(() => {
    const unsubscribe = subscribeOrbieeVoicePlayback((state) => {
      if (state === "playing") {
        voicePlaybackActiveRef.current = true;
        setIsAssistantSpeaking(true);
        clearAudioGraceTimer();
        return;
      }
      voicePlaybackActiveRef.current = false;
      setIsAssistantSpeaking(false);
      maybeNotifyVoiceTurnComplete();
    });
    return unsubscribe;
  }, [clearAudioGraceTimer, maybeNotifyVoiceTurnComplete]);

  // Turn finished with no streamed audio — hand mic back after grace window.
  useEffect(() => {
    if (!isVoiceMode || isChatLoading) {
      clearAudioGraceTimer();
      return undefined;
    }

    if (voicePlaybackActiveRef.current || isOrbieeVoicePlaying()) {
      return undefined;
    }

    clearAudioGraceTimer();
    audioGraceTimerRef.current = setTimeout(() => {
      audioGraceTimerRef.current = null;
      if (voicePlaybackActiveRef.current || isOrbieeVoicePlaying()) return;
      maybeNotifyVoiceTurnComplete();
    }, NO_AUDIO_GRACE_MS);

    return clearAudioGraceTimer;
  }, [
    isVoiceMode,
    isChatLoading,
    clearAudioGraceTimer,
    maybeNotifyVoiceTurnComplete,
  ]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearVoiceRestartTimer();
      if (recordingRef.current) {
        try {
          recordingRef.current.stopRecording();
        } catch (_) {
          /* ignore */
        }
        recordingRef.current = null;
      }
      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach((track) => track.stop());
        mediaStreamRef.current = null;
      }
    };
  }, [clearVoiceRestartTimer]);

  return (
    <Box
      // Where an agent add's fly-to-bag ghost leaves from when the chat is closed.
      data-fly-agent="avatar"
      ref={drag.containerRef}
      // Dragged position (left/top) overrides the sx corner when present.
      style={drag.style}
      sx={{
        position: "fixed",
        bottom: { xs: 12, sm: 16 },
        right: { xs: 12, sm: 16 },
        zIndex: 40,
        // Width drives the whole widget (the frame below is locked to 2:3), so
        // it's tied to the window height and capped: full size on a tall
        // window, a step smaller on a short one (e.g. 100% browser zoom on a
        // scaled display) instead of crowding the page.
        width: { xs: "min(140px, 30vh)", sm: "min(192px, 26vh)" },
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      {/* Avatar — fixed size; voice controls live in a sibling row below.
          Also the drag handle on desktop. */}
      <Box
        ref={drag.handleRef}
        {...drag.handleProps}
        sx={{
          width: "100%",
          borderRadius: "var(--ob-r-lg)",
          overflow: "hidden",
          mb: 0,
          position: "relative",
          touchAction: drag.enabled ? "none" : undefined,
          transition: drag.isDragging
            ? "none"
            : "box-shadow .3s ease, transform .3s cubic-bezier(.2,.8,.2,1)",
          // The frame keeps the avatar video's own 2:3 portrait shape. It used
          // to be 30vh tall against a fixed 192px width, so the box changed
          // shape with the window height — at 100% browser zoom on a scaled
          // display it went nearly square and squashed her. maxHeight only
          // shortens the frame on a very short window; the canvas is `contain`,
          // so that letterboxes into transparency instead of distorting.
          aspectRatio: "2 / 3",
          maxHeight: "46vh",
          backgroundColor: "transparent",

          // Recording is the one state that earns a hard ring; everything
          // else is carried by the soft elevation below.
          border:
            !isVoiceMode && isAvatarRecording
              ? `3px solid ${alpha("#cf2f2f", 0.8)}`
              : "1px solid var(--ob-line-2)",
          boxShadow:
            !isVoiceMode && isAvatarRecording
              ? `0 0 0 4px ${alpha("#cf2f2f", 0.28)}, var(--ob-shadow-lg)`
              : "var(--ob-shadow)",
          animation:
            !isVoiceMode && isAvatarRecording
              ? "pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite"
              : "none",
          "&:hover": {
            boxShadow:
              !isVoiceMode && !isAvatarRecording
                ? "var(--ob-shadow-lg)"
                : undefined,
            transform:
              !isVoiceMode && !isAvatarRecording && !drag.isDragging
                ? "translateY(-3px)"
                : undefined,
          },
          "&:hover .ob-avatar-grip": { opacity: 0.55 },
          cursor: drag.isDragging
            ? "grabbing"
            : drag.enabled
              ? "grab"
              : isVoiceMode
                ? "default"
                : "pointer",
          "@keyframes pulse": {
            "0%, 100%": {
              opacity: 1,
            },
            "50%": {
              opacity: 0.5,
            },
          },
        }}
        onClick={isVoiceMode ? undefined : (e) => handleAvatarClick?.(e)}
        title={
          isVoiceMode
            ? drag.enabled
              ? "Drag to move"
              : undefined
            : isAvatarRecording
              ? "Click to stop"
              : drag.enabled
                ? "Click to talk to Orbiee · drag to move"
                : "Click to talk to Orbiee"
        }
      >
        {/* Grip hint — the whole frame drags; this only says so on hover. */}
        {drag.enabled && (
          <Box
            className="ob-avatar-grip"
            aria-hidden="true"
            sx={{
              position: "absolute",
              top: 8,
              left: "50%",
              transform: "translateX(-50%)",
              zIndex: 25,
              display: "grid",
              gridTemplateColumns: "repeat(3, 3px)",
              gap: "3px",
              padding: "4px 6px",
              borderRadius: "99px",
              background: "rgba(255, 255, 255, 0.7)",
              opacity: drag.isDragging ? 0.55 : 0,
              transition: "opacity .2s",
              pointerEvents: "none",
              "& > span": {
                width: 3,
                height: 3,
                borderRadius: "99px",
                background: "var(--ob-ink-2)",
              },
            }}
          >
            <span /><span /><span /><span /><span /><span />
          </Box>
        )}
        <Box
          sx={{
            position: "relative",
            width: "100%",
            height: "100%",
            backgroundColor: "transparent",
          }}
        >
          {/* Keep avatar mounted (idle/WebRTC unchanged); only hide visually in voice mode */}
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              visibility: isVoiceMode ? "hidden" : "visible",
              pointerEvents: isVoiceMode ? "none" : "auto",
            }}
            aria-hidden={isVoiceMode}
          >
            <ConnectToRealTimeAvatargetVideo
              onAvatarVideoLoadingChange={setIsAvatarVideoLoading}
            />
          </Box>

          {isVoiceMode && (
            <VoiceModeVisual
              audioLevel={audioLevel}
              isRecording={isRecording}
              isLoading={isChatLoading}
              isMicMuted={isMicMuted}
              isSpeaking={isAssistantSpeaking}
            />
          )}
        </Box>
      </Box>

      {/* Voice mode controls — below avatar, outside chat box */}
      {isVoiceMode && (
        <VoiceModeControls
          isRecording={isRecording}
          isMicMuted={isMicMuted}
          isLoading={isChatLoading}
          onToggleMicMute={toggleMicMute}
          onStopRecording={cancelVoiceRecording}
          onStartVoiceMode={startVoiceMode}
        />
      )}

      {/* Talk to Orbiee — the one call to action under the portrait. A
          design-system button (ob-btn-agent) so it is the same sky fill and
          indigo ink as every other agent control. Hidden while the picker is
          open, while the video is still arriving, and in voice mode: the
          voice controls stand in its place, and "talk" is already happening.
          (Choosing Voice closes the picker, so "picker closed" alone would
          show this button under the voice pill.) Opens signed out too. */}
      {!showControlBar && !isVoiceMode && !isAvatarVideoLoading && (
        <button
          type="button"
          className="ob-btn ob-btn-agent ob-btn-block ob-talkbtn"
          onClick={() => setShowControlBar(true)}
          aria-haspopup="true"
          aria-expanded={false}
        >
          <Sparkles size={14} strokeWidth={1.9} aria-hidden="true" />
          Talk to Orbiee
        </button>
      )}

      {/* Mode picker — the Talk button, opened: the same sky pill, the same
          width, now holding a back chevron and two round buttons, Voice and
          Chat. (A labelled two-row card was tried and rejected by the user;
          the pill is the shape they want.) The icons do the explaining —
          a waveform for voice, a bubble for chat — and each has a tooltip.
          Choosing a mode closes the pill: the Talk button is the resting
          state. Hidden in voice mode so the portrait does not shift. */}
      {showControlBar && !isVoiceMode && (
        <div
          className="ob-modepill"
          role="group"
          aria-label="Talk to Orbiee"
          onKeyDown={(e) => {
            if (e.key === "Escape") setShowControlBar(false);
          }}
        >
          <button
            type="button"
            className="ob-modeback"
            onClick={() => setShowControlBar(false)}
            aria-label="Back"
            title="Back"
          >
            <ChevronLeft size={16} strokeWidth={2} />
          </button>

          <button
            type="button"
            className="ob-modebtn"
            onClick={() => {
              setShowControlBar(false);
              handleMicButtonClick();
            }}
            aria-label="Voice — speak out loud"
            title="Voice — speak out loud"
            autoFocus
          >
            <AudioLines size={17} strokeWidth={1.9} />
          </button>

          <button
            type="button"
            className="ob-modebtn"
            onClick={handleChatClick}
            aria-label="Chat — type a message"
            title="Chat — type a message"
          >
            <MessageCircleMore size={17} strokeWidth={1.9} />
          </button>
        </div>
      )}

      {/* Chat Box — portalled to <body> so it stays fixed to the viewport
          whatever happens to the widget it lives in (drag, animation). */}
      {createPortal(
        <ChartBoxCode
          isOpen={isChatOpen}
          messages={chatMessages}
          inputValue={chatInputValue}
          setInputValue={setChatInputValue}
          onClose={() => {
            sessionStorage.setItem("orbiee_chat_manually_closed", "true");
            setIsChatOpen(false);
          }}
          onSendMessage={handleSendMessage}
          onKeyPress={handleChatKeyPress}
          isCartCollapsed={true}
          setIsCartCollapsed={() => { }}
          totalItems={0}
          onSuggestionClick={(answer) => {
            // Handle suggestion click
            setChatInputValue(answer);
            setTimeout(() => handleSendMessage(), 100);
          }}
          isLoading={isChatLoading}
          activity={chatActivity}
          followUps={chatFollowUps}
          onFollowUpClick={(text) => handleSendMessage(text)}
          sessionTakeover={sessionTakeover}
          onResumeSession={handleResumeSession}
          listenAvatarEvents={false}
        />,
        document.body
      )}


      {/* Virtual Try-On Modal */}
      <VirtualTryOn
        open={tryOnOpen}
        autoStart={tryOnAutoStart}
        onClose={() => {
          notifyVirtualTryOnClosed();
          setTryOnOpen(false);
          setTryOnProductId(null);
          setTryOnAutoStart(null);
        }}
        productImage=""
        article_id={tryOnProductId}
      />
    </Box>
  );
}
